import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, Clock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const blogPosts = [
  {
    id: 1,
    slug: 'beneficios-terapia-online-anonima',
    title: '5 beneficios de la terapia online anónima que quizás no conocías',
    excerpt: 'Descubre cómo el anonimato puede potenciar tu proceso terapéutico y ayudarte a abrirte más con tu psicólogo.',
    category: 'Bienestar',
    author: 'Equipo AnonimaMente',
    date: '2026-01-15',
    readTime: 5,
    image: 'https://images.unsplash.com/photo-1546975313-c2a873d092d8?w=600&h=400&fit=crop'
  },
  {
    id: 2,
    slug: 'ansiedad-laboral-chile',
    title: 'Ansiedad laboral en Chile: cifras y cómo buscar ayuda sin estigma',
    excerpt: 'El 40% de los trabajadores chilenos reporta síntomas de ansiedad. Conoce las señales y opciones de apoyo.',
    category: 'Salud Laboral',
    author: 'Equipo AnonimaMente',
    date: '2026-01-10',
    readTime: 7,
    image: 'https://images.unsplash.com/photo-1759419049045-26c6ea811419?w=600&h=400&fit=crop'
  },
  {
    id: 3,
    slug: 'primera-sesion-psicologo',
    title: 'Qué esperar de tu primera sesión con un psicólogo online',
    excerpt: 'Guía práctica para quienes nunca han ido a terapia. Resolvemos mitos y preparamos tu primera experiencia.',
    category: 'Guías',
    author: 'Equipo AnonimaMente',
    date: '2026-01-05',
    readTime: 6,
    image: 'https://images.unsplash.com/photo-1766603065399-8048428595bd?w=600&h=400&fit=crop'
  }
];

const BlogPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="bg-teal-600 py-16 md:py-24">
        <div className="container-max mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-white/20 text-white hover:bg-white/20 mb-4">
              <BookOpen className="w-3 h-3 mr-1" />
              Blog
            </Badge>
            <h1 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
              Recursos de bienestar mental
            </h1>
            <p className="text-lg text-teal-100 leading-relaxed">
              Artículos, guías y recursos para cuidar tu salud mental. 
              Información confiable escrita por profesionales.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="section-padding">
        <div className="container-max mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Card key={post.id} className="overflow-hidden border-0 shadow-soft card-hover">
                <div className="aspect-[16/10] overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Badge variant="secondary" className="bg-teal-50 text-teal-700">
                      {post.category}
                    </Badge>
                    <span className="text-sm text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime} min
                    </span>
                  </div>
                  
                  <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3 line-clamp-2">
                    {post.title}
                  </h2>
                  
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <User className="w-4 h-4" />
                      <span>{post.author}</span>
                    </div>
                    <span className="text-sm text-slate-500">
                      {new Date(post.date).toLocaleDateString('es-CL', { 
                        day: 'numeric', 
                        month: 'short' 
                      })}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-500 mb-4">
              Más artículos próximamente...
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
            ¿Listo para dar el primer paso?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Leer es útil, pero la acción transforma. Agenda tu primera sesión anónima hoy.
          </p>
          <Link to="/agendar">
            <Button className="btn-primary">
              Agendar sesión
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
